import type React from 'react'

import { formatName, STAFF_ROLE_LABELS, type StaffMember } from '@snootsvet/shared'

import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/frontend/components/ui/combobox'
import { Skeleton } from '@/frontend/components/ui/skeleton'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useListStaff } from '@/frontend/hooks/api/use-list-staff'

interface StaffSearchProps {
  id?: string
  /** The selected staff id (or null if none selected). */
  value: string | null
  /** Called with the full staff object when a selection is made, or null when cleared. */
  onChange?: (staff: StaffMember | null) => void
  /** Optional predicate to narrow which staff appear in the dropdown (e.g. only vets, or only bookable staff). */
  filter?: (staff: StaffMember) => boolean
  onBlur?: () => void
  disabled?: boolean
  placeholder?: string
  /** Whether the user can clear the selection. Defaults to `false`. */
  allowClear?: boolean
  /** Which side the dropdown opens on. Defaults to `'bottom'`. */
  side?: 'bottom' | 'top'
  /** Override collision avoidance behaviour for the dropdown positioner. */
  collisionAvoidance?: React.ComponentProps<typeof ComboboxContent>['collisionAvoidance']
  'aria-invalid'?: boolean
  className?: string
}

/**
 * Type-to-search staff picker over the active region's staff. Owns its data via
 * `useListStaff`, shows a skeleton while loading, and reports selections as the
 * full StaffMember object.
 */
function StaffSearch({
  id,
  value,
  onChange,
  filter,
  onBlur,
  disabled,
  placeholder = 'Search staff...',
  allowClear = false,
  side,
  collisionAvoidance,
  className,
  ...props
}: StaffSearchProps) {
  const { data: staff, isLoading } = useListStaff()
  const { region } = useViewerRegion()

  const allStaff = staff ?? []
  // Options shown in the dropdown: the region's staff, narrowed by the optional filter predicate.
  const inRegion = allStaff.filter((staff) => staff.regions.includes(region))
  const items = filter ? inRegion.filter(filter) : inRegion
  // Resolve the selected id against the full list so a selection still displays
  // even if it's out of region or the filter would exclude it.
  const selected = value ? (allStaff.find((member) => member.id === value) ?? null) : null

  if (isLoading) {
    return <Skeleton className={className ?? 'h-9 w-full'} />
  }

  return (
    <Combobox<StaffMember>
      value={selected}
      onValueChange={(member) => {
        if (!member && !allowClear) return
        onChange?.(member ?? null)
      }}
      isItemEqualToValue={(a, b) => a.id === b.id}
      itemToStringLabel={(member) => formatName(member) || member.email}
      items={items}
      disabled={disabled}
    >
      <ComboboxInput
        id={id}
        placeholder={placeholder}
        onBlur={onBlur}
        aria-invalid={props['aria-invalid']}
        showClear={allowClear}
        className={className}
      />
      <ComboboxContent className="min-w-60" side={side} collisionAvoidance={collisionAvoidance}>
        <ComboboxList>
          <ComboboxEmpty>No staff found.</ComboboxEmpty>
          <ComboboxCollection>
            {/* ComboboxCollection erases its generic and types children as (item: any) => ReactNode.
                 Accessing any.role is fine, but STAFF_ROLE_LABELS[any] triggers TS7053 because
                 indexing a Record<StaffRole, string> with 'any' is not allowed. clinic-search
                 avoids this — it only reads plain properties off the item, not an indexed lookup. */}
            {(member: StaffMember) => (
              <ComboboxItem key={member.id} value={member}>
                <div className="flex flex-col">
                  <span>{formatName(member) || member.email}</span>
                  <span className="text-sm text-muted-foreground">
                    {STAFF_ROLE_LABELS[member.role]}
                  </span>
                </div>
              </ComboboxItem>
            )}
          </ComboboxCollection>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

export { StaffSearch }
